// Screw eligibility: the filters that populate the family / diameter / length / washer selects (compatibility with
// connection type, service class, thicknesses, minimum member sizes, CLT/LVL rules, thread rules, penetration).

import { currentAlphaElem1, currentAlphaElem2, currentFi1, currentFi2, fmtN } from "./angles";
import type { ScrewRecord } from "./catalog";
import { tryCurrentClt, type CltLayup } from "./clt";
import { screwRecordOf } from "./controls";
import { isPanelMat, isSteelMat, isThreeD, isThreeEl, isTwoSides, pInt, type EngineCtx } from "./ctx";
import { TIMBER_TIPO_MAP_REVERSE, WASHERS_DISABLED } from "./data";
import { panelScrewOk, type ElementN } from "./materials";
import { currentDiametro, etaForoNonPredrilledFamilyPure, isHbsFamily, isLvlAxialEdgeFamily, isLvlWideFamily } from "./spacing";

// Compatible washer codes of a screw (rondelleCompatibili: null/"none"/comma-separated), minus WASHERS_DISABLED.
export function rondelleCompatibiliList(screwRec: ScrewRecord | null): string[] {
  const raw = screwRec && screwRec.rondelleCompatibili;
  if (!raw || raw.toLowerCase() === "none") return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s && !WASHERS_DISABLED.has(s));
}
// Family + diameter + length → record (first family/diameter match when the length is not valid).
export function currentScrewRecord(ctx: EngineCtx): ScrewRecord | null {
  return screwRecordOf(ctx.v, ctx.cat);
}

// Families compatible with the connection type (moduliCompatibili "Acciaio-Legno"/"Legno-Legno" == 2).
export function currentModuloConnessioneKey(ctx: EngineCtx): string {
  return isSteelMat(ctx.v.materiale1) ? "Acciaio-Legno" : "Legno-Legno";
}
// EU corrosion class SC_EU ("SC2"…) = highest EC5 service class the screw is suitable for; null = no constraint.
export function scEuMaxClasse(scEu: string | null | undefined): number | null {
  const m = /^SC(\d+)$/i.exec(scEu || "");
  return m ? parseInt(m[1] ?? "", 10) : null;
}
export function currentClasseServizio(ctx: EngineCtx): number {
  return pInt(ctx.v.classeServizio) || 1;
}

/* THREE-MEMBER CONNECTIONS (user request 2026-10-12): legno3 → element 1 central (loaded, rotated by θ), elements 2
   and 3 lateral and identical; screws from both outer sides: head on element 2 (and 3), tip in element 1, each side
   a single shear (nPiani = 2). legno3d/pannello3d → one screw through the three members (double shear). */
export function headN(ctx: EngineCtx): 1 | 2 {
  return isThreeEl(ctx.v.materiale1) ? 2 : 1; // member crossed by the head
}
export function pointN(ctx: EngineCtx): 1 | 2 {
  return isThreeD(ctx.v.materiale1) ? 2 : isThreeEl(ctx.v.materiale1) ? 1 : 2; // member of the tip (double shear: 3 = data of 2)
}
export function tHeadDom(ctx: EngineCtx): number {
  // double shear: the screw crosses element 2 + element 1 before entering element 3
  return isThreeD(ctx.v.materiale1) ? ctx.v.spessore2 + ctx.v.spessore1 : headN(ctx) === 1 ? ctx.v.spessore1 : ctx.v.spessore2;
}
export function tPointDom(ctx: EngineCtx): number {
  return pointN(ctx) === 1 ? ctx.v.spessore1 : ctx.v.spessore2;
}
// [head side, tip side] labels
export function tLabels(ctx: EngineCtx): [string, string] {
  return isThreeD(ctx.v.materiale1) ? ["(t2+t1)", "t3"] : isThreeEl(ctx.v.materiale1) ? ["t2", "t1"] : ["t1", "t2"];
}
// Tip margin from the opposite face of the tip member: 10 mm (user request 2026-10-08). Two-sided connections
// (EN 1995-1-1 §8.3.1.1(7), fig. 8.5: screws of the two sides may overlap in the central member if (t − t2) > 4d):
// max(4d, 10 mm).
export const SCREW_LENGTH_TIP_MARGIN_MM = 10;
export function tipMarginMm(ctx: EngineCtx, d: number): number {
  return isTwoSides(ctx.v.materiale1) && Number.isFinite(d) ? Math.max(4 * d, SCREW_LENGTH_TIP_MARGIN_MM) : SCREW_LENGTH_TIP_MARGIN_MM;
}

// With a washer the length limits apply to L − h (manual design only: the solution search ignores the washer here).
export function currentWasherH(ctx: EngineCtx): number {
  if (ctx.solutionsRunning) return 0;
  if (!ctx.v.useRondella) return 0;
  const w = ctx.cat.WASHER_DB[ctx.v.rondellaCodice];
  return w && Number.isFinite(w.h) ? w.h : 0;
}
// t1 + 4d < L − h < t1 + t2 − margin, with (t1, t2) = (head side, tip side).
export function screwLengthFitsElements(ctx: EngineCtx, L: number, t1: number, t2: number, d: number): boolean {
  if (!Number.isFinite(L) || !Number.isFinite(t1) || !Number.isFinite(t2)) return true;
  const Leff = L - currentWasherH(ctx);
  if (Leff >= t1 + t2 - tipMarginMm(ctx, d)) return false;
  if (Number.isFinite(d) && Leff <= t1 + 4 * d) return false;
  return true;
}

// HBS table "non-predrilled, d = 6–12 mm, softwood members with minimum thickness 10·d and width 10·d"
// (ETA-11/0030): checked for each timber element (solid/glulam softwood only).
export function etaMinMemberSizeOk(d: number, t: number, b: number): boolean {
  if (!Number.isFinite(d) || !Number.isFinite(t) || !Number.isFinite(b)) return true;
  return t >= 10 * d && b >= 10 * d;
}
const SOFTWOOD_SUBSTRATES: (string | undefined)[] = ["Solidwood Softwood", "Glulam Softwood"];
export function screwFitsMinMemberSize(ctx: EngineCtx, family: string, d: number): boolean {
  const v = ctx.v;
  if (v.etaForo !== "nonPredrilled") return true;
  if (!Number.isFinite(d) || d < 6 || d > 12) return true;
  if (!isHbsFamily(family)) return true;
  if (!isSteelMat(v.materiale1)) {
    const clt1 = tryCurrentClt(ctx, 1);
    const substrate1 = clt1 ? "CLT Softwood" : TIMBER_TIPO_MAP_REVERSE[v.mat1Tipo];
    if (SOFTWOOD_SUBSTRATES.includes(substrate1)) {
      if (!etaMinMemberSizeOk(d, v.spessore1, v.b1)) return false;
    }
  }
  const clt2 = tryCurrentClt(ctx, 2);
  const substrate2 = clt2 ? "CLT Softwood" : TIMBER_TIPO_MAP_REVERSE[v.mat2Tipo];
  if (SOFTWOOD_SUBSTRATES.includes(substrate2)) {
    if (!etaMinMemberSizeOk(d, v.spessore2, v.b2)) return false;
  }
  return true;
}
// CLT (ETA-11/0030): minimum panel thickness 10·d (wide and narrow; the narrow penetration ≥ 10·d depends on the
// length, see screwFitsPenetrationDepth).
export function cltFitsMinThickness(clt: CltLayup | null, d: number): boolean {
  if (!clt || !Number.isFinite(d)) return true;
  return clt.spessoreTotale >= 10 * d;
}
// LVL softwood (ETA-11/0030): wide face (HBS/HBSH/HBSP/SHS/KKF/TBS, 5≤d≤10) side member t ≥ 8.4·d − 9 mm; edge/axial
// (VGZ/VGZH/VGS/VGSH, d = 7/9) panel t ≥ 45/57 mm. Applied to both elements when LVL.
export function screwFitsLvlMinThickness(ctx: EngineCtx, family: string, d: number): boolean {
  const elementOk = (n: 1 | 2): boolean => {
    const clt = tryCurrentClt(ctx, n);
    if (clt) return true; // CLT has its own rule
    const substrate = TIMBER_TIPO_MAP_REVERSE[n === 1 ? ctx.v.mat1Tipo : ctx.v.mat2Tipo];
    if (substrate !== "LVL Softwood") return true;
    const t = n === 1 ? ctx.v.spessore1 : ctx.v.spessore2;
    if (!Number.isFinite(t)) return true;
    if (isLvlWideFamily(family) && d >= 5 && d <= 10 && t < 8.4 * d - 9) return false;
    if (isLvlAxialEdgeFamily(family) && (d === 7 || d === 9)) {
      const needPanel = d === 7 ? 45 : 57;
      if (t < needPanel) return false;
    }
    return true;
  };
  if (!isSteelMat(ctx.v.materiale1) && !elementOk(1)) return false;
  return elementOk(2);
}

// PARTIAL thread screws (threadType 'partial'): 4·d < LG1 ≤ t2 + 20 mm (user request 2026-10-08). The smooth shank
// rule A = L − LG1 ≥ t1 − 20 applies only as PREFILTER of the automatic solution search (partialAStrict), dropped if
// no screw is left. Not with a steel plate as element 1.
export const PARTIAL_THREAD_A_MARGIN_MM = 20;
export const PARTIAL_THREAD_LG1_EXCESS_MM = 20;
export function screwFitsPartialThread(ctx: EngineCtx, family: string, d: number, L: number): boolean {
  if (isSteelMat(ctx.v.materiale1)) return true;
  const rec = ctx.cat.screwIndexByKey.get(family + "|" + d + "|" + L);
  if (!rec || rec.threadType !== "partial") return true;
  const t1 = tHeadDom(ctx),
    t2 = tPointDom(ctx);
  if (!Number.isFinite(t1) || !Number.isFinite(rec.geometria_mm.LG1)) return true;
  const LG1 = rec.geometria_mm.LG1;
  if (Number.isFinite(d) && !(LG1 > 4 * d + 1e-6)) return false;
  if (ctx.partialAStrict && rec.geometria_mm.L - LG1 < t1 - PARTIAL_THREAD_A_MARGIN_MM - 1e-6) return false;
  return !Number.isFinite(t2) || LG1 <= t2 + PARTIAL_THREAD_LG1_EXCESS_MM + 1e-6;
}
// FULL thread screws in timber-timber: about half the screw in each member: |L/2 − t1| ≤ 20 and t2 ≥ L/2 − 10.
export const FULL_THREAD_TOL_MM = 20;
export const FULL_THREAD_T2_MARGIN_MM = 10;
export function screwFitsFullThreadCentering(ctx: EngineCtx, family: string, d: number, L: number): boolean {
  if (isSteelMat(ctx.v.materiale1)) return true;
  const rec = ctx.cat.screwIndexByKey.get(family + "|" + d + "|" + L);
  if (!rec || rec.threadType !== "full") return true;
  const t1 = tHeadDom(ctx),
    t2 = tPointDom(ctx);
  if (!Number.isFinite(t1) || !Number.isFinite(t2)) return true;
  const half = L / 2;
  return Math.abs(half - t1) <= FULL_THREAD_TOL_MM + 1e-6 && t2 >= half - FULL_THREAD_T2_MARGIN_MM - 1e-6;
}
// Length-dependent ETA rules on the penetration depth (L − h − t1) in the tip member: CLT narrow ≥ 10·d; LVL edge
// VGZ/VGS d = 7/9: axial member depth ≥ 100/120 mm. Also the thread rules above.
export function screwFitsPenetrationDepth(ctx: EngineCtx, family: string, d: number, L: number): boolean {
  if (!Number.isFinite(L) || !Number.isFinite(d)) return true;
  if (!screwFitsPartialThread(ctx, family, d, L)) return false;
  if (!screwFitsFullThreadCentering(ctx, family, d, L)) return false;
  const t1 = tHeadDom(ctx);
  if (!Number.isFinite(t1)) return true;
  const depth = L - currentWasherH(ctx) - t1;
  const pn = pointN(ctx);
  const clt2 = tryCurrentClt(ctx, pn);
  if (clt2) {
    if (clt2.latoInserimento === "narrow") return depth >= 10 * d;
    return true;
  }
  const substrate2 = TIMBER_TIPO_MAP_REVERSE[pn === 1 ? ctx.v.mat1Tipo : ctx.v.mat2Tipo];
  if (substrate2 === "LVL Softwood" && isLvlAxialEdgeFamily(family) && (d === 7 || d === 9)) {
    const needH = d === 7 ? 100 : 120;
    return depth >= needH;
  }
  return true;
}

// EC5 §8.3.1.2(6)/(7), eq. (8.18)/(8.19): minimum timber thickness to avoid predrilling; splitting-prone species
// (Douglas checkbox) unless a4 ≥ 10d (ρk≤420) / 14d (420<ρk≤500).
export function ec5MinThicknessNails(d: number, rhok: number, isSplittingProne: boolean, a4: number): number | null {
  if (!Number.isFinite(d) || !Number.isFinite(rhok)) return null;
  const base = Math.max(7 * d, ((13 * d - 30) * rhok) / 400); // (8.18)
  if (!isSplittingProne) return base;
  const a4OkPerEsenzione =
    Number.isFinite(a4) && ((rhok <= 420 && a4 >= 10 * d) || (rhok > 420 && rhok <= 500 && a4 >= 14 * d));
  return a4OkPerEsenzione ? base : Math.max(14 * d, ((13 * d - 30) * rhok) / 200); // (8.19)
}
// ETA-11/0030 thresholds by diameter, only with "Preforo"; other diameters fall back to the EC5 formula.
export function etaMinThicknessPredrilledBreakpoint(d: number): number | null {
  if (!Number.isFinite(d)) return null;
  if (d < 8) return 24;
  if (d === 8) return 30;
  if (d === 9) return 40;
  if (d === 11) return 80;
  if (d === 13) return 100;
  return null;
}
// Predrilled: diameter threshold when defined and a1/a3,t/a3,c of the base table are all ≥ 25d; otherwise EC5.
export function screwMinThicknessRequired(
  ctx: EngineCtx,
  family: string,
  d: number,
  alphaDeg: number,
  substrateKey: string | undefined,
  rhok: number,
  isSplittingProne: boolean,
): number | null {
  if (!Number.isFinite(d) || !Number.isFinite(rhok)) return null;
  const sp = etaForoNonPredrilledFamilyPure(family, d, alphaDeg, substrateKey, rhok);
  if (ctx.v.etaForo === "predrilled") {
    const bp = etaMinThicknessPredrilledBreakpoint(d);
    const cond25d = sp.a1 >= 25 * d && sp.a3t >= 25 * d && sp.a3c >= 25 * d;
    if (bp != null && cond25d) return bp;
  }
  return ec5MinThicknessNails(d, rhok, isSplittingProne, Math.min(sp.a4t, sp.a4c));
}
export function screwFitsMinThicknessRule(ctx: EngineCtx, family: string, d: number): boolean {
  if (/KKT/i.test(family || "")) return true;
  const v = ctx.v;
  const alphaGrainElem1 = currentAlphaElem1(ctx) - currentFi1(ctx);
  const alphaGrainElem2 = currentAlphaElem2(ctx) - currentFi2(ctx);
  if (!isSteelMat(v.materiale1)) {
    const clt1 = tryCurrentClt(ctx, 1);
    const substrate1 = clt1 ? "CLT Softwood" : TIMBER_TIPO_MAP_REVERSE[v.mat1Tipo];
    if (SOFTWOOD_SUBSTRATES.includes(substrate1)) {
      const need1 = screwMinThicknessRequired(ctx, family, d, alphaGrainElem1, substrate1, v.rhok1, v.douglas1);
      if (need1 != null && Number.isFinite(v.spessore1) && v.spessore1 < need1) return false;
    }
  }
  const clt2 = tryCurrentClt(ctx, 2);
  const substrate2 = clt2 ? "CLT Softwood" : TIMBER_TIPO_MAP_REVERSE[v.mat2Tipo];
  if (SOFTWOOD_SUBSTRATES.includes(substrate2)) {
    const need2 = screwMinThicknessRequired(ctx, family, d, alphaGrainElem2, substrate2, v.rhok2, v.douglas2);
    if (need2 != null && Number.isFinite(v.spessore2) && v.spessore2 < need2) return false;
  }
  return true;
}

// All the filters of a screw candidate (populateFamiglie / findCompatibleSolutions).
function passaModuloESC(ctx: EngineCtx, s: ScrewRecord, moduloKey: string, classeServizio: number): boolean {
  if (!s.moduliCompatibili || s.moduliCompatibili[moduloKey] !== 2 || !panelScrewOk(ctx, s)) return false;
  const scMax = scEuMaxClasse(s.materiale && s.materiale.SC_EU);
  return scMax == null || scMax >= classeServizio;
}
export function screwPassesAllFilters(
  ctx: EngineCtx,
  s: ScrewRecord,
  env: { moduloKey: string; classeServizio: number; t1: number; t2: number; isSteel: boolean; clt1: CltLayup | null; clt2: CltLayup | null },
): boolean {
  const g = s.geometria_mm;
  return (
    passaModuloESC(ctx, s, env.moduloKey, env.classeServizio) &&
    screwLengthFitsElements(ctx, g.L, env.t1, env.t2, g.d1) &&
    screwFitsMinMemberSize(ctx, s.family, g.d1) &&
    screwFitsMinThicknessRule(ctx, s.family, g.d1) &&
    (env.isSteel || cltFitsMinThickness(env.clt1, g.d1)) &&
    cltFitsMinThickness(env.clt2, g.d1) &&
    screwFitsLvlMinThickness(ctx, s.family, g.d1) &&
    screwFitsPenetrationDepth(ctx, s.family, g.d1, g.L)
  );
}
export function filterEnv(ctx: EngineCtx) {
  return {
    moduloKey: currentModuloConnessioneKey(ctx),
    classeServizio: currentClasseServizio(ctx),
    t1: tHeadDom(ctx), // (t1, t2) = (head side, tip side)
    t2: tPointDom(ctx),
    isSteel: isSteelMat(ctx.v.materiale1),
    clt1: tryCurrentClt(ctx, 1),
    clt2: tryCurrentClt(ctx, 2),
  };
}

// Predrilling imposed by ETA-11/0030 (p. 8 and 14): carbon steel screws when the member's characteristic density
// exceeds 550 kg/m³, stainless steel ones above 500 kg/m³, in any timber (hardwood included). Exempt: HBS HARDWOOD,
// LBS and LBS HARDWOOD, VGS/VGZ HARDWOOD and KKT (the «EVO» variants are only a coating and follow the base family);
// carbon HBS PLATE only in hardwood (with or without predrilling), HBS PLATE A4 never. While the rule applies «Tipologia
// foro» is forced to «Preforo» and locked; it is released (back to the value chosen before) as soon as it no longer
// applies. Element 1 is only considered when it is timber.
const PREDRILL_EXEMPT_RE = /^(HBS HARDWOOD|LBS|VGS HARDWOOD|VGZ HARDWOOD|KKT)/i;
function screwIsStainless(ctx: EngineCtx, family: string): boolean {
  const rec = ctx.cat.SCREWS_DB.find((s) => s.family === family);
  return /stainless|A4|A2|AISI/i.test(rec?.materiale.material ?? "");
}
export function predrillRequirement(ctx: EngineCtx, family: string): { required: boolean; reasons: string[] } {
  const v = ctx.v;
  const out = { required: false, reasons: [] as string[] };
  if (!family || PREDRILL_EXEMPT_RE.test(family)) return out;
  const inox = screwIsStainless(ctx, family),
    limit = inox ? 500 : 550;
  const plateCarbon = /^HBS PLATE/i.test(family) && !inox;
  const els: ElementN[] = isSteelMat(v.materiale1) || isPanelMat(v.materiale1) ? [2] : [1, 2];
  for (const n of els) {
    const rho = v[`rhok${n}` as const];
    const tipo = TIMBER_TIPO_MAP_REVERSE[v[`mat${n}Tipo` as const]];
    const specie = v[`mat${n}Specie` as const];
    const hard = tipo === "Solidwood Hardwood" || (tipo === "LVL Softwood" && /beech|faggio|FST/i.test(specie));
    if (hard && plateCarbon) continue; // ETA p. 14: carbon HBS PLATE in hardwood with or without predrilling
    if (Number.isFinite(rho) && rho > limit) {
      out.required = true;
      out.reasons.push(ctx.t.engine.predrill.reason(fmtN(rho, 0), limit, n, hard, inox));
    }
  }
  return out;
}
// #etaForo forced/locked by the rule; v.etaForoUser keeps the user's choice (HTML dataset.userValue).
export function enforcePredrillByRule(ctx: EngineCtx): void {
  const { v, ui } = ctx;
  const req = predrillRequirement(ctx, v.etaFamiglia);
  if (req.required) {
    if (!ui.disabled.etaForo) v.etaForoUser = v.etaForo;
    v.etaForo = "predrilled";
    ui.disabled.etaForo = true;
    ui.texts.etaForoForced = ctx.t.engine.predrill.forced(req.reasons.join("; "));
  } else {
    if (ui.disabled.etaForo) {
      ui.disabled.etaForo = false;
      if (v.etaForoUser) v.etaForo = v.etaForoUser;
      v.etaForoUser = null;
    }
    ui.texts.etaForoForced = null;
  }
}

const fmtT = (t: number) => (Number.isFinite(t) ? t.toFixed(0) : "—");

// Families: only those with AT LEAST ONE screw passing all the filters (manual input: no smooth-shank prefilter).
// Fallback to the first family when the current one is not in the list.
export function populateFamiglie(ctx: EngineCtx): void {
  const cur = ctx.v.etaFamiglia;
  const env = filterEnv(ctx);
  const famiglieSenzaLunghezza = new Set(
    ctx.cat.SCREWS_DB.filter((s) => passaModuloESC(ctx, s, env.moduloKey, env.classeServizio)).map((s) => s.family),
  );
  const famiglieCompatibili = new Set(ctx.cat.SCREWS_DB.filter((s) => screwPassesAllFilters(ctx, s, env)).map((s) => s.family));
  const famiglie = [...new Set(ctx.cat.SCREWS_DB.map((s) => s.family))].filter((f) => famiglieCompatibili.has(f));
  if (famiglie.length === 0 && famiglieSenzaLunghezza.size > 0) {
    const [l1, l2] = tLabels(ctx);
    ctx.ui.texts.etaFamigliaEmptyWarn = ctx.t.engine.screwLists.noFamily(`${l1}=${fmtT(env.t1)}`, `${l2}=${fmtT(env.t2)}`);
  } else {
    ctx.ui.texts.etaFamigliaEmptyWarn = null;
  }
  ctx.options.families = famiglie;
  ctx.v.etaFamiglia = famiglie.includes(cur) ? cur : (famiglie[0] ?? "");
}

// Diameters of the family having at least one compatible length; default = the first (smallest).
export function populateDiametri(ctx: EngineCtx): void {
  const fam = ctx.v.etaFamiglia;
  const cur = ctx.v.etaDiametro;
  const db = ctx.cat.SCREWS_DB;
  const tuttiIDiametri = [...new Set(db.filter((s) => s.family === fam && panelScrewOk(ctx, s)).map((s) => s.geometria_mm.d1))]
    .filter((d) => typeof d === "number")
    .sort((a, b) => a - b);
  const t1 = tHeadDom(ctx);
  const t2 = tPointDom(ctx);
  const isSteelDiam = isSteelMat(ctx.v.materiale1);
  const cltDiam1 = tryCurrentClt(ctx, 1);
  const cltDiam2 = tryCurrentClt(ctx, 2);
  const diametri = tuttiIDiametri.filter((d) => {
    if (!screwFitsMinMemberSize(ctx, fam, d)) return false;
    if (!screwFitsMinThicknessRule(ctx, fam, d)) return false;
    if (!isSteelDiam && !cltFitsMinThickness(cltDiam1, d)) return false;
    if (!cltFitsMinThickness(cltDiam2, d)) return false;
    if (!screwFitsLvlMinThickness(ctx, fam, d)) return false;
    const lunghezzeFamDiam = db
      .filter((s) => s.family === fam && s.geometria_mm.d1 === d && panelScrewOk(ctx, s))
      .map((s) => s.geometria_mm.L);
    return lunghezzeFamDiam.some((l) => screwLengthFitsElements(ctx, l, t1, t2, d) && screwFitsPenetrationDepth(ctx, fam, d, l));
  });
  if (diametri.length === 0 && tuttiIDiametri.length > 0) {
    const [l1, l2] = tLabels(ctx);
    ctx.ui.texts.etaDiametroEmptyWarn = ctx.t.engine.screwLists.noDiameter(`${l1}=${fmtT(t1)}`, `${l2}=${fmtT(t2)}`);
  } else {
    ctx.ui.texts.etaDiametroEmptyWarn = null;
  }
  ctx.options.diameters = diametri.map((d) => ({ value: d, label: d.toFixed(1).replace(/\.0$/, "") + " mm" }));
  if (diametri.length === 0) {
    ctx.v.etaDiametro = NaN;
    return;
  }
  ctx.v.etaDiametro = diametri.includes(cur) ? cur : diametri[0]!;
}

// Lengths of family + diameter compatible with the thicknesses (empty list + warning when none).
export function populateLunghezze(ctx: EngineCtx): void {
  const fam = ctx.v.etaFamiglia;
  const d = currentDiametro(ctx);
  const cur = ctx.v.etaLunghezza;
  const recs = ctx.cat.SCREWS_DB.filter((s) => s.family === fam && s.geometria_mm.d1 === d && panelScrewOk(ctx, s));
  const tutteLeLunghezze = [...new Set(recs.map((s) => s.geometria_mm.L))].filter((l) => typeof l === "number").sort((a, b) => a - b);
  const t1 = tHeadDom(ctx);
  const t2 = tPointDom(ctx);
  const lunghezze = tutteLeLunghezze.filter((l) => screwLengthFitsElements(ctx, l, t1, t2, d) && screwFitsPenetrationDepth(ctx, fam, d, l));
  if (lunghezze.length === 0 && tutteLeLunghezze.length > 0) {
    const [l1, l2] = tLabels(ctx);
    const wh = currentWasherH(ctx);
    ctx.ui.texts.etaLunghezzaEmptyWarn = ctx.t.engine.screwLists.noLength(
      (t1 + 4 * d + wh).toFixed(0),
      (t1 + t2 - tipMarginMm(ctx, d) + wh).toFixed(0),
      wh,
      `${l1}=${fmtT(t1)}`,
      `${l2}=${fmtT(t2)}`,
    );
  } else {
    ctx.ui.texts.etaLunghezzaEmptyWarn = null;
  }
  ctx.options.lengths = lunghezze.map((l) => ({
    value: l,
    label: l.toFixed(0) + " mm",
    screwId: recs.find((s) => s.geometria_mm.L === l)?.id ?? null,
  }));
  if (lunghezze.length === 0) {
    ctx.v.etaLunghezza = NaN;
    return;
  }
  ctx.v.etaLunghezza = lunghezze.includes(cur) ? cur : lunghezze[0]!;
}

// Washers compatible with the current screw: none → field hidden and "Usa rondella" unchecked; otherwise keeps the
// current code if compatible, else the first.
export function populateRondelle(ctx: EngineCtx): void {
  const codici = rondelleCompatibiliList(currentScrewRecord(ctx));
  ctx.options.washers = codici;
  if (codici.length === 0) {
    ctx.ui.visible.rondellaField = false;
    ctx.v.useRondella = false;
    ctx.v.rondellaCodice = "";
    updateRondellaSelectVisibility(ctx);
    return;
  }
  ctx.ui.visible.rondellaField = true;
  const cur = ctx.v.rondellaCodice;
  ctx.v.rondellaCodice = codici.includes(cur) ? cur : codici[0]!;
  updateRondellaSelectVisibility(ctx);
}
export function updateRondellaSelectVisibility(ctx: EngineCtx): void {
  ctx.ui.visible.rondellaCodice = ctx.v.useRondella;
}
